import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import { RetailerView as RetailerModal } from "../../common/retail-cash-flow/retailer-view";
import XButton from "assets/icons/x_button.svg";
import CircleInfo from "assets/icons/info_icon_solid.svg";
import triangleWarning from "assets/icons/triangle_exclamation.svg";
import {
  useCurrencyFormatter,
  useDateFormatter,
  useLabels,
  usePaymentHistory,
  useRetailer,
} from "hooks";
import { getBiller } from "selectors";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import {
  Banner,
  BannerProps,
  Button,
  ButtonRow,
  TextButton,
  Tooltip,
  TooltipEnums,
} from "kubra-ux-forge";
import { Navigate, useNavigate } from "react-router";
import { CardType } from "kubra-ux-forge/dist/foundation/CreditCardLogos/CreditCardLogo";
import { IReceiptInfo } from "../../common/receipt-info-modal";
import { ACTION } from "actions/paymentIntentActions";
import {
  getPayableFromSession,
  postPayablePrepareIntent,
} from "api/IamApi/entities/ezpay";
import { getI18n, useTranslation } from "react-i18next";
import {
  BALANCE_NAMESPACE,
  COMMON_NAMESPACE,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  PAYMENT_RECEIPT_NAMESPACE,
} from "constants/i18n-namespaces";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { PageLoader } from "components/ezpay/common/page-loader";
import { setAppLoading } from "actions/appStateActions";
import { DigitalPaymentData } from "types/digitalPaymentData";
import { IDigitalWallet } from "types/paymentIntent";
import { payablePaymentIntentPrepareRequest } from "types/Api/Payment/PayablePaymentIntentPrepareRequest";
import useWallet from "hooks/useWallet";
import FundingOption, {
  ApplePay,
  getCardNetworkByShortCode,
  GooglePay,
  PayPal,
  translateBankAccountType,
  Venmo,
} from "constants/FundingOption";
import _ from "lodash";
import { PaymentDateModal } from "../../common/payment-date-modal";
import { ManagePaymentMethodsModal } from "../../common/manage-payment-methods-modal";
import moment from "moment";
import routing from "routing";
import {
  getDomain,
  getLabel,
  mapCharityToCharityState,
  phoneFormat,
} from "utilities/helper";
import { customStyles } from "constants/customStyles";
import { CashView } from "../../common/retail-cash-flow/cash-view";
import { getPayablePaymentIntent } from "selectors/payablePaymentIntent";
import { SelectPaymentMethodModal } from "../../common/select-payment-method-modal";
import { CashModal } from "../../common/retail-cash-flow/cash-modal";
import { PayableBankAccountModal } from "../../common/payable-bank-account-modal";
import {
  isExpiryCardDateValid,
  PayableCreditDebitModal,
} from "../../common/payable-credit-debit-modal";
import { PaymentAmountModal } from "../../common/payment-amount-modal";
import {
  getBankAccount,
  getCreditDebitCard,
  getPaymentIntent,
  getPaymentMethod,
  getWallet,
} from "selectors/paymentIntent";
import { ICharityState } from "types/charity";
import { Donation } from "../../common/donation";
import { IDonation } from "types/Api/Payment/Donation";
import { AccountPayable, InvoicePayable } from "types/payable";
import { getPayableSession } from "selectors/payableSession";
import { setPayableSession } from "actions/payableSessionActions";
import IamApi from "api/IamApi";
import { USER_LOGOUT } from "actions/userActions";
import { IPayableSessionData } from "types/payableSession";
import { PayableGooglePayButtonWrapper } from "../../common/payable-google-pay-button";
import PayablePayPalButtonWrapper from "../../common/payable-paypal-button/payable-paypal-button-wrapper";
import { PayableApplePayButtonWrapper } from "../../common/payable-apple-pay-button";
import { getIsMobile } from "selectors/appState";
import { MAX_RECENT_PAYMENTS } from "../../common/payable-recent-payments";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";

//components for displaying the various views within payment method modal
const methodComponents = {
  SelectPaymentMethodModal,
  CashModal,
  RetailerModal,
  PayableBankAccountModal,
  PayableCreditDebitModal,
};
export type MethodComponentsOptions = keyof typeof methodComponents;

export const castToCardType = (
  cardTypeString: string
): CardType | undefined => {
  try {
    const cardType: CardType = cardTypeString.toLowerCase() as CardType;
    return cardType;
  } catch (error) {
    return undefined;
  }
};

enum PaymentAllowed {
  NotDefined,
  AnyAmount,
  BilledAmountOrLess,
  BilledAmountOnly,
  NoPaymentAllowed,
}

export const PayableDashboard = () => {
  const { t } = useTranslation(BALANCE_NAMESPACE);
  const isMobile = useSelector(getIsMobile);

  const { handleSubmit } = useForm();

  const payableSession = useSelector(getPayableSession);

  const [amountModalIsOpen, setAmountModalIsOpen] = useState(false);
  const [methodModalIsOpen, setMethodModalIsOpen] = useState(false);
  const [dateModalIsOpen, setDateModalIsOpen] = useState(false);
  const [retailerModalIsOpen, setRetailerModalIsOpen] = useState(false);

  const [managePaymentMethodModalIsOpen, setManagePaymentMethodModalIsOpen] =
    useState(false);

  // TODO: initialize current payment amount to the payable amount.
  // Consider if we need to initialize it to what's in the Redux store?
  // For instance when we come back from review page to payment page,
  // we would like to see the amount that the user entered instead of
  // defaulting to the payable amount.
  // UPDATE #2: More and more leaning towards having the current payment amount be kept in Redux and read from there
  // instead of duplicating it in local state here in this component. Need to get back to working on this asap.
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState(
    payableSession?.payable?.payableAmount ?? 0.0
  );

  const [activeMethodComponent, setActiveMethodComponent] =
    useState<MethodComponentsOptions>("SelectPaymentMethodModal");

  const [bannerState, setBannerState] = useState<BannerProps>({
    active: false,
    variant: "info",
    messageLabel: "",
  });

  const [displayMethodBack, setDisplayMethodBack] = useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [totalErrorMessage, setTotalErrorMessage] = useState("");
  const [paymentMethodError, setPaymentMethodError] = useState("");

  const [isDataLoading, setIsDataLoading] = useState(true);

  const [loadingStates, setLoadingStates] = useState({
    loadConfigWalletPayments: true,
    setAccountBalanceAndOverpayment: true,
    setWalletAsDefaultPaymentMethod: true,
  });

  const [paymentAllowedState, setPaymentAllowedState] =
    useState<PaymentAllowed>(PaymentAllowed.NotDefined);

  const currencyFormatter = useCurrencyFormatter();
  const dateFormatter = useDateFormatter();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const finalBiller = useSelector(getBiller);
  const { loadLabels, getLabels } = useLabels();
  const labelSource = getLabels();

  const { loadBillerConfiguration, getBillerConfiguration } = useRetailer();
  const { loadWalletItems, getWalletItems } = useWallet(true);
  const {
    loadRecentPayments,
    loadUpcomingPayment,
    getUpcomingPayment,
    getRecentPayments,
  } = usePaymentHistory(true);

  const walletItems = getWalletItems();
  const bankAccount = useSelector(getBankAccount);
  const creditDebitCard = useSelector(getCreditDebitCard);
  const wallet = useSelector(getWallet);
  const paymentMethod = useSelector(getPaymentMethod);
  const billerConfiguration = getBillerConfiguration();
  const upcomingPayment = getUpcomingPayment();
  const recentPayments = getRecentPayments();

  const CurrentMethodComponent: any = methodComponents[activeMethodComponent];

  // TODO: review the payable payment intent state object here.
  // It doesn't match the payment intent state in the old flow.
  // Here it's matching the response of the payable initiate payment intent endpoint.
  // Whereas the old payment intent state holds more state (i.e. charities, current UI payment amount etc.)
  // The options for us seems to be:
  // 1) eliminate the payable payment intent and use the old payment intent state and just keep updating things in it from the new endpoints.
  // 2) update the payable payment intent state to match the old one. Though consider if there really is any benefit to this? Like would there be
  // any difference between the two "payment intent" states?
  const payablePaymentIntentState = useSelector(getPayablePaymentIntent);
  // TODO: review the use of the payment intent state. That state is used in the old flow.
  // But I'm utilizing it here because some of the components (like payment date modal) update their
  // state in the Redux store (and not through callbacks), so to get the selected payment date we have
  // to get from Redux store. The other options are we create a new payable payment date modal that
  // returns either the selected date through a callback or updates the state in the new "payable payment intent state".
  const paymentIntentState = useSelector(getPaymentIntent);
  const todayDateString = dateFormatter.shortIsoFormat(new Date(), "en-US");
  const i18n = getI18n();
  const [emailBannerState, setEmailBannerState] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsBannerState, setSmsBannerState] = useState(false);
  const [isCashView, setIsCashView] = useState(false);

  // Clear error message when user changes the language
  useEffect(() => {
    setPaymentErrorMessage("");
    // scrolls to top in Safari
    document.body.scrollTop = 0;
    // scrolls to top in Chrome, Firefox, IE, and Opera
    document.documentElement.scrollTop = 0;
    // Mobile view
    window.scrollTo({ top: 0, behavior: "smooth" });

    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
  }, [i18n.language]);

  useEffect(() => {
    if (
      !bankAccount &&
      !creditDebitCard &&
      !wallet &&
      !(paymentMethod == GooglePay) &&
      !(paymentMethod == ApplePay) &&
      !(paymentMethod == PayPal) &&
      !(paymentMethod == Venmo) &&
      walletItems &&
      walletItems.length > 0
    ) {
      const preferredWallet =
        walletItems.find((w) => w.isPreferred) || walletItems[0];

      const isRestricted =
        payableSession?.payable?.fundingRestrictions?.includes(
          preferredWallet?.fundingOption ?? ""
        );

      if (preferredWallet !== undefined && !isRestricted) {
        dispatch({ type: ACTION.ADD_WALLET, payload: preferredWallet });
        dispatch({
          type: ACTION.ADD_PAYMENT_METHOD,
          payload: FundingOption.Wallet,
        });
      } else {
        dispatch({ type: ACTION.ADD_WALLET, payload: null });
        dispatch({
          type: ACTION.ADD_PAYMENT_METHOD,
          payload: "",
        });
      }
      updateLoadingState("setWalletAsDefaultPaymentMethod", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletItems, bankAccount, creditDebitCard, wallet]);

  useEffect(() => {
    // Load config and wallet and recent payments
    const loadConfigWalletPayments = async () => {
      if (finalBiller !== null && finalBiller !== undefined) {
        try {
          await Promise.all([
            loadBillerConfiguration(finalBiller.clientId, finalBiller.realm),
            loadWalletItems(
              finalBiller.clientId,
              finalBiller.featureFlags.accountV2
            ),
            loadRecentPayments(
              MAX_RECENT_PAYMENTS,
              finalBiller.clientId,
              finalBiller.realm
            ),
            loadUpcomingPayment(finalBiller.clientId, finalBiller.realm),
          ]);
        } catch (error) {
          console.log(
            "Error loading config, wallet and recent payments:",
            error
          );
        } finally {
          updateLoadingState("loadConfigWalletPayments", false);
        }
      } else {
        updateLoadingState("loadConfigWalletPayments", false);
      }
    };

    loadConfigWalletPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBiller]);

  useEffect(() => {
    // scroll to top when banner shows up
    if (bannerState.active) {
      // scrolls to top in Safari
      document.body.scrollTop = 0;
      // scrolls to top in Chrome, Firefox, IE, and Opera
      document.documentElement.scrollTop = 0;
      // Mobile view
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [bannerState.active]);

  // Monitoring loadingStates and updating isDataLoading accordingly
  useEffect(() => {
    const allDone = Object.values(loadingStates).every(
      (state) => state === false
    );
    if (allDone && payableSession) {
      setIsDataLoading(false);
    }
  }, [loadingStates, payableSession]);

  // TODO: move this away from useEffect. Should be able to do that as part of the render cycle.
  // Update payment allowed state
  useEffect(() => {
    if (payableSession?.payable) {
      updatePaymentAllowedState(payableSession?.payable?.payableAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (paymentIntentState.charities) {
      // Deselect all charities
      paymentIntentState.charities?.forEach((c) => updateCharity(c.charity.code, { selected: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Get charities.
  const charities = payableSession?.charities?.filter(
    (ch: any) => ch.code != null && ch.name != null
  );

  const [charitiesState, setCharitiesState] = useState(
    mapCharityToCharityState(charities)
  );

  if (!payableSession.payable || !payableSession?.paymentIntentId) {
    console.error(
      "Payable is not available in the session or the intent is not initialized"
    );

    return <Navigate to={routing.index()} replace />;
  }

  const setAmountModalIsOpenToTrue = () => {
    // TODO Need to check for proper place to set payableAmount in paymentIntentState
    dispatch({
      type: ACTION.ADD_PAYMENT_AMOUNT,
      payload: payableSession?.payable?.payableAmount,
    });
    setAmountModalIsOpen(true);
  };

  const setMethodModalIsOpenToTrue = () => {
    setDisplayMethodBack(false);
    setMethodModalIsOpen(true);
    setBannerState((prevState) => ({ ...prevState, active: false }));
  };

  const setAmountModalIsOpenToFalse = () => {
    document.body.style.removeProperty("overflow");
    setAmountModalIsOpen(false);
    setPaymentErrorMessage("");
    setBannerState((prevState) => ({ ...prevState, active: false }));
  };

  const setMethodModalIsOpenToFalse = () => {
    document.body.style.removeProperty("overflow");
    setMethodModalIsOpen(false);

    setActiveMethodComponent("SelectPaymentMethodModal");
    setPaymentMethodError("");
  };

  const setRetailerModalStatus = () => {
    if (activeMethodComponent === "RetailerModal") {
      setRetailerModalIsOpen(true);
    }
  };

  const updateCurrentPaymentAmount = (amount: number) => {
    // TODO: do we need to update the payment amount in the intent state?
    // Basically we need to ask our selves what's the source of truth for the current payment amount?
    // The state variable in this component or the Redux store?
    // The payment intent state in Redux is also kind of bloated holding state that doesn't
    // really belong to a "payment intent" (as in the Payment Intent API). For instance charities are
    // not part of a payment intent. Likewise the current payment amount is a property of the UI state
    // not "payment intent" state so it shouldn't even be in the "payment intent state" in the first place.
    setCurrentPaymentAmount(amount);
    setPaymentErrorMessage("");
    setTotalErrorMessage("");
    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
    setAmountModalIsOpenToFalse();
  };

  const updateLoadingState = (key: string, value: boolean) => {
    setLoadingStates((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleMethodChange = (method: MethodComponentsOptions) => {
    setActiveMethodComponent(method);
    if (method === "SelectPaymentMethodModal") {
      setDisplayMethodBack(false);
    } else setDisplayMethodBack(true);
  };

  const paymentDateClicked = () => {
    if (futureDateDisabled) return;

    setDateModalIsOpen(true);
  };

  // get required values from subresources
  const imageResources: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "ImageResources";
  });

  const externalLinks: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "ExternalLinks";
  });

  const contactUs: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  });

  const payableType = payableSession?.payableType;

  const handleCharitySelection = (charityCode: string, selected: boolean) => {
    updateCharity(charityCode, { selected });
  };

  const handleCharityAmountChange = (charityCode: string, amount: number) => {
    updateCharity(charityCode, { amount });
  };

  const updateCharity = (
    charityCode: string,
    updateValue: Partial<ICharityState>
  ) => {
    if (updateValue.selected !== undefined && !updateValue.selected) {
      updateValue.amount = 0;
    }

    const newCharitiesState = charitiesState.map((charityState) => {
      return charityState.charity.code !== charityCode
        ? charityState
        : {
            ...charityState,
            ...updateValue,
          };
    });
    setCharitiesState(newCharitiesState);
    dispatch({ type: ACTION.ADD_CHARITIES, payload: newCharitiesState });
  };

  // Add charities amount
  const charitiesTotal = charitiesState
    .filter((ch) => ch.selected)
    .reduce((amount, charity) => amount + charity.amount, 0);

  const parseDueDate = (dueDate: string) => {
    return dueDate
      ? dateFormatter.shortFormat(
          new Date(moment(dueDate).utc().toString()),
          "en-US"
        )
      : "";
  };

  let paymentMethodLabel = t("select-one");

  if (isCashView) {
    paymentMethodLabel = t("cash-method");
  } else if (bankAccount != null) {
    paymentMethodLabel =
      translateBankAccountType(bankAccount.accountType, t) +
      " *" +
      bankAccount.bankAccountNumber.substring(
        bankAccount.bankAccountNumber.length - 4
      );
  } else if (creditDebitCard != null) {
    paymentMethodLabel =
      getCardNetworkByShortCode(creditDebitCard.cardType) +
      " *" +
      creditDebitCard.cardNo.substring(creditDebitCard.cardNo.length - 4);
  } else if (wallet != null) {
    let label = "";
    if (wallet.fundingOption === FundingOption.Card) {
      label =
        getCardNetworkByShortCode(wallet.presentableMOPCode) +
        " " +
        wallet.maskedNumber;
      if (!isExpiryCardDateValid(wallet.expiry)) {
        setBannerState({
          active: true,
          variant: "warning",
          messageLabel: t("card-expired-warning", { card: label }),
          withClose: true,
          withIcon: true,
          withHyperlink: true,
          hyperlink: () => setManagePaymentMethodModalIsOpen(true),
          hyperlinkText: t("manage-payment-methods-button", {
            ns: PAYMENT_METHOD_MODAL_NAMESPACE,
          }),
        });
        // setWarningMessage(t("card-expired-warning", { card: label }));
        // reset wallet
        dispatch({ type: ACTION.ADD_WALLET, payload: null });
        dispatch({
          type: ACTION.ADD_PAYMENT_METHOD,
          payload: "",
        });
      }
    } else {
      // translate bank account type
      label =
        translateBankAccountType(wallet.presentableMOPCode, t) +
        " " +
        wallet.maskedNumber;
    }
    paymentMethodLabel = label;
  }

  if (paymentMethod == FundingOption.GooglePay) {
    paymentMethodLabel = t("G Pay");
  }

  if (paymentMethod == FundingOption.ApplePay) {
    paymentMethodLabel = t("Apple Pay");
  }

  if (paymentMethod == FundingOption.PayPal) {
    paymentMethodLabel = t("Paypal");
  }

  if (paymentMethod == FundingOption.Venmo) {
    paymentMethodLabel = t("Venmo");
  }

  const getSelectedCharities = (): IDonation[] | undefined => {
    if (
      paymentIntentState.charities == null ||
      paymentIntentState.charities?.length == 0
    ) {
      return undefined;
    }

    return paymentIntentState.charities
      .filter((c) => c.selected && c.amount > 0)
      .map((selectedCharity) => ({
        charityCode: selectedCharity.charity.code,
        amount: selectedCharity.amount,
      }));
  };

  const getPayablePrepareRequest =
    (): payablePaymentIntentPrepareRequest | null => {
      let selectedFsOption = "";
      if (bankAccount != null) {
        selectedFsOption = FundingOption.BankAccount;
      } else if (creditDebitCard != null) {
        selectedFsOption = FundingOption.Card;
      } else if (wallet != null) {
        selectedFsOption = FundingOption.Wallet;
      }

      // TODO: review if we should get the currentPaymentAmount from paymentIntentState?
      if (selectedFsOption === FundingOption.Card) {
        return {
          paymentAmount: currentPaymentAmount,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          card: {
            card: creditDebitCard.cardNo.replace(/\s/g, ""),
            cvc: creditDebitCard.securityCode,
            cardType: getCardNetworkByShortCode(creditDebitCard.cardType),
            expiry: creditDebitCard.cardExpiry,
            zip: creditDebitCard.zipCode,
            holderName:
              creditDebitCard.firstName + " " + creditDebitCard.lastName,
            saveToWallet: creditDebitCard.saveToWallet,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      } else if (selectedFsOption === FundingOption.BankAccount) {
        return {
          paymentAmount: currentPaymentAmount,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          bank: {
            bankNumber: bankAccount.bankAccountNumber,
            routingNumber: bankAccount.routingNumber,
            accountType: bankAccount.accountType,
            holderName: bankAccount.accountOwnerName,
            saveToWallet: bankAccount.saveToWallet,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      } else if (selectedFsOption === FundingOption.Wallet) {
        let fundingType = wallet.fundingOption;
        if (wallet.fundingOption === "Card") {
          if (wallet.presentableMOPCode === "AX") {
            fundingType = "CreditCardAmex";
          }
          else {
            fundingType = "CreditCard";
          }
        }
        return {
          paymentAmount: currentPaymentAmount,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          wallet: {
            fundingSourceId: wallet.fundingSourceId,
            type: fundingType,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      }

      return null;
    };

  const onSubmit = (data: any) => {
    // @ts-ignore
    //buttonRef.current.setLoadingToTrue();
    //setLoading(true);
    // - set up next step in ez-pay flow
  };

  const reviewDigitalPayment = (paymentData: DigitalPaymentData) => {
    setMethodModalIsOpenToFalse();

    // update payment intent
    const digitalWallet: IDigitalWallet = {
      cardType: paymentData.cardType,
      paymentToken: paymentData.paymentToken,
    };
    dispatch({
      type: ACTION.ADD_DIGITAL_WALLET,
      payload: digitalWallet,
    });
    const receiptInfo: IReceiptInfo = {
      mobileNumber: paymentData.mobileNumber ?? "",
      mobileAlert: false,
      email: paymentData.email,
      emailAlert: false,
      alertEnabled: false,
    };

    dispatch({ type: ACTION.ADD_RECEIPTINFO, payload: receiptInfo });
    dispatch({
      type: ACTION.ADD_PAYMENT_METHOD,
      payload: paymentData.paymentMethod,
    });
    dispatch({
      type: ACTION.ADD_SERVICE_FEE,
      payload: paymentData.serviceFee,
    });
  };

  const resetSelectedPaymentMethod = () => {
    // TODO: review this further. May be we need to create a state in Redux where we can store
    // the payment method information. May be this can be the new "payable payment intent state".
    // The new payable payment intent doesn't currently have accomodations for that and may be we
    // have to expand it???
    // There are new payment method modal components (bank, credit & debit cards) that
    // supposedly use/update their state to the new payment intent state because
    // there are design changes for those modals. But that doesn't necessarily mean
    // that we can't use the old payment intent state with those new components.
    // If there is no need for a new payable payment intent state then just update
    // those new components to use the old payment intent state.
    // For now we're resorting to using the old payment intent state so that we can run this page with no errors.
    paymentIntentState.creditDebitCard = null;
    paymentIntentState.bankAccount = null;
    paymentIntentState.wallet = undefined;
    paymentIntentState.digitalWallet = undefined;
  };

  const verifyPaymentAmountForDigitalWallet = () => {
    const isValidAmount = isAmountPayable();
    if (!isValidAmount.isValid) {
      setMethodModalIsOpenToFalse();
      return { isValid: false };
    }
    return { isValid: true };
  };

  const handlePrepareIntentErrorForDigitalWallet = (
    err: any,
    payablePrepareRequest: payablePaymentIntentPrepareRequest
  ) => {
    resetSelectedPaymentMethod();
    handlePayablePrepareIntentErrors(err, payablePrepareRequest);
    setMethodModalIsOpenToFalse();
  };

  const isAmountPayable = () => {
    // TODO: review this payable null check as we shouldn't have to check this again here since we're
    // throwing an error in the render cycle if the payable is not available in the session.
    // So it should not even be possible to get here.
    if (!payableSession?.payable) {
      return { isValid: false };
    }

    // TODO: shouldn't minAmount be fetched from the payable type configuration? It may not always be 1.
    const minAmount = 1;
    if (payableSession?.payable?.payableAmount === 0) {
      setPaymentErrorMessage(t("overpayment-enabled-no-amount-error"));
      setErrorMessageInBanner(t("payment-amount-invalid"));
      return { isValid: false };
    }

    // TODO: review if we can eliminate the paymentAllowedState and get the payment settings from the payable type definition instead?
    if (
      payableType.allowPartialPayment &&
      payableSession?.payable?.payableAmount < minAmount
    ) {
      setPaymentErrorMessage(t("invalid-min-payment-amount"));
      setErrorMessageInBanner(t("invalid-minimum-payment-banner"));
      return { isValid: false };
    }

    if (
      paymentAllowedState === PaymentAllowed.BilledAmountOrLess &&
      currentPaymentAmount > payableSession?.payable?.payableAmount
    ) {
      setPaymentErrorMessage(
        t("overpayment-disabled-amount-error", {
          amount: payableSession?.payable?.payableAmount,
        })
      );
      setErrorMessageInBanner(t("overpayment-disabled-banner-error"));
      return { isValid: false };
    }

    if (
      paymentAllowedState === PaymentAllowed.BilledAmountOnly &&
      currentPaymentAmount !== payableSession?.payable?.payableAmount
    ) {
      setPaymentErrorMessage(
        t("over-and-partial-payment-disabled-banner-error")
      );
      setErrorMessageInBanner(
        t("over-and-partial-payment-disabled-banner-error")
      );
      return { isValid: false };
    }

    // if no error, reset the error message
    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
    return { isValid: true };
  };

  const checkIsCashOnly = () => {
    if (
      !payablePaymentIntentState?.fundingSources ||
      payablePaymentIntentState?.fundingSources.length === 0
    )
      return false;

    if (
      payablePaymentIntentState?.fundingSources.length === 1 &&
      payablePaymentIntentState?.fundingSources.includes(FundingOption.Cash)
    )
      return true;

    return false;
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const payPalWrapper = useMemo(() => {
    if (payableSession.paypalConfiguration !== undefined) 
    {
      return (        
        <PayablePayPalButtonWrapper
          currency="USD"
          amount={currentPaymentAmount + charitiesTotal}
          paymentIntentId={payableSession.paymentIntentId}
          clientId={finalBiller.clientId}
          reviewDigitalPayment={reviewDigitalPayment}
          config={payableSession.paypalConfiguration}
          verifyPaymentAmount={
            verifyPaymentAmountForDigitalWallet
          }
          onPrepareIntentError={
            handlePrepareIntentErrorForDigitalWallet
          }
        ></PayablePayPalButtonWrapper>     
      );
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentState.paymentAmount, finalBiller.clientId, paymentIntentState.intentId]);

  const getSelectedPaymentMethod = (
    payablePrepareRequest: payablePaymentIntentPrepareRequest
  ) => {
    if (payablePrepareRequest.fundingOption === FundingOption.Card) {
      return "Card";
    } else if (
      payablePrepareRequest.fundingOption === FundingOption.BankAccount
    ) {
      return "BankAccount";
    } else if (payablePrepareRequest.fundingOption === FundingOption.Wallet) {
      if (payablePrepareRequest.wallet?.type === "Card") {
        return "Card";
      } else if (payablePrepareRequest.wallet?.type === "BankAccount") {
        return "BankAccount";
      }
    }
    // return selected digital wallet
    return payablePrepareRequest.fundingOption;
  };

  const setErrorMessageInBanner = (message: string) => {
    setBannerState((prev) => ({
      ...prev,
      active: true,
      variant: "error",
      messageLabel: message,
      withClose: true,
      withHyperlink: false,
      withIcon: true,
    }));
  };

  const handlePayablePrepareIntentErrors = (
    err: any,
    payablePrepareRequest: payablePaymentIntentPrepareRequest
  ) => {
    const selectedPaymentMethod = getSelectedPaymentMethod(
      payablePrepareRequest
    );

    switch (err.errorMessageCode) {
      // if payment method is invalid, code will be 24/25/26/45
      case "24":
      case "25":
      case "26":
      case "45":
        setErrorMessageInBanner(t("payment-method-invalid"));
        return;

      // if payment velocity error, code will be 50/51
      case "50":
      case "51":
        setErrorMessageInBanner(t("payment-velocity-exceeded-error"));
        return;

      // if payment amount is invalid, code will be 30/31
      case "30":
      case "31":
        if (
          !payableType.allowPartialPayment &&
          currentPaymentAmount > err.errorMessageValue
        ) {
          if (
            selectedPaymentMethod === "Card" ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-amount-partial-payments-card-banner", {
                method:
                  selectedPaymentMethod === "Card"
                    ? t("card")
                    : selectedPaymentMethod,
                phone: phoneFormat(contactUs.properties.phone),
              })
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-amount-partial-payments-bank-banner", {
                phone: phoneFormat(contactUs.properties.phone),
              })
            );
            return;
          }
        } else if (payableSession.charities != undefined) {
          // TODO: review this again, no idea how having charities relates to the error handling here?
          if (err.errorMessageType === "MinAmount") {
            if (
              selectedPaymentMethod === "Card" ||
              selectedPaymentMethod === "GooglePay" ||
              selectedPaymentMethod === "ApplePay" ||
              selectedPaymentMethod === "PayPal" ||
              selectedPaymentMethod === "Venmo"
            ) {
              setErrorMessageInBanner(
                t("invalid-min-amount-card-banner", {
                  amount: err.errorMessageValue,
                  method:
                    selectedPaymentMethod === "Card"
                      ? t("card")
                      : selectedPaymentMethod,
                })
              );
              setTotalErrorMessage(
                t("payment-total-error", { amount: err.errorMessageValue })
              );
              return;
            } else {
              setErrorMessageInBanner(
                t("invalid-min-amount-bank-banner", {
                  amount: err.errorMessageValue,
                })
              );
              setTotalErrorMessage(
                t("payment-total-error", { amount: err.errorMessageValue })
              );
              return;
            }
          } else if (
            selectedPaymentMethod === "Card" ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-max-amount-card-banner", {
                amount: err.errorMessageValue,
                method:
                  selectedPaymentMethod === "Card"
                    ? t("card")
                    : selectedPaymentMethod,
              })
            );
            setTotalErrorMessage(
              t("payment-total-error", { amount: err.errorMessageValue })
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-max-amount-bank-banner", {
                amount: err.errorMessageValue,
              })
            );
            setTotalErrorMessage(
              t("payment-total-error", { amount: err.errorMessageValue })
            );
            return;
          }
        } else if (err.errorMessageType === "MinAmount") {
          if (
            selectedPaymentMethod === "Card" ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-min-amount-card-banner", {
                amount: err.errorMessageValue,
                method:
                  selectedPaymentMethod === "Card"
                    ? t("card")
                    : selectedPaymentMethod,
              })
            );
            setPaymentErrorMessage(
              t("payment-min-amount-error", {
                amount: err.errorMessageValue,
              })
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-min-amount-bank-banner", {
                amount: err.errorMessageValue,
              })
            );
            setPaymentErrorMessage(
              t("payment-min-amount-error", {
                amount: err.errorMessageValue,
              })
            );
            return;
          }
        } else if (
          selectedPaymentMethod === "Card" ||
          selectedPaymentMethod === "GooglePay" ||
          selectedPaymentMethod === "ApplePay" ||
          selectedPaymentMethod === "PayPal" ||
          selectedPaymentMethod === "Venmo"
        ) {
          setErrorMessageInBanner(
            t("invalid-max-amount-card-banner", {
              amount: err.errorMessageValue,
              method:
                selectedPaymentMethod === "Card"
                  ? t("card")
                  : selectedPaymentMethod,
            })
          );
          setPaymentErrorMessage(
            t("payment-max-amount-error", {
              amount: err.errorMessageValue,
            })
          );
          return;
        } else {
          setErrorMessageInBanner(
            t("invalid-max-amount-bank-banner", {
              amount: err.errorMessageValue,
            })
          );
          setPaymentErrorMessage(
            t("payment-max-amount-error", {
              amount: err.errorMessageValue,
            })
          );
          return;
        }
      default:
        setErrorMessageInBanner(t("process-payment-error"));
        return;
    }
  };

  const handleContinueClick = () => {
    // validate required fields
    const isAmountValid = isAmountPayable();
    if (!isAmountValid.isValid) {
      return;
    }

    if (bankAccount == null && creditDebitCard == null && wallet == null) {
      setPaymentMethodError(t("no-payment-method-selected-error"));
    }

    if (
      paymentIntentState.charities != undefined &&
      paymentIntentState.charities?.filter((c) => c.selected && c.amount == 0)
        .length > 0
    ) {
      setErrorMessageInBanner(t("donation-amount-required"));
      return;
    }

    if (paymentIntentState.charities != undefined && 
      paymentIntentState.charities?.filter((c) => c.selected && c.amount > 1).length != paymentIntentState.charities?.filter((x) => x.selected).length
    ) {
      setErrorMessageInBanner(t("donation-amount-invalid"));
      return;
    }

    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));

    const payablePrepareRequest = getPayablePrepareRequest();

    if (payablePrepareRequest === null) {
      console.error("Prepare request is null");
      return;
    }

    dispatch(setAppLoading(true));

    postPayablePrepareIntent(payablePrepareRequest)
      .catch((err) => {
        console.error("Error calling payable prepare endpoint", err);
        handlePayablePrepareIntentErrors(err, payablePrepareRequest);
        return Promise.reject(err);
      })
      .then(() => getPayableFromSession(getDomain()))
      .then((response: IPayableSessionData) => {
        dispatch(setPayableSession(response));
      })
      .catch((err) => {
        console.error("Error fetching payable session after prepare", err);
        return Promise.reject(err);
      })
      .then(() => navigate(routing.payableReview.index()))
      .catch((err) => {
        console.log("error = ", err);
      })
      .finally(() => dispatch(setAppLoading(false)));
  };

  let maxScheduledDays = -1;
  if (payableType.maxScheduledDays) {
    maxScheduledDays = payableType.maxScheduledDays;
  }

  let maxDueDate = moment().startOf("day").toDate();

  if (maxScheduledDays !== undefined && maxScheduledDays > 0) {
    maxDueDate = moment(payableSession?.payable?.dueDate)
      .startOf("day")
      .toDate();
    
    maxDueDate.setDate(maxDueDate.getDate() + maxScheduledDays);
  }

  const futureDateDisabled =
    !_.isEmpty(upcomingPayment) ||
    moment(maxDueDate).isBefore(moment().startOf("day").toDate()) ||
    maxScheduledDays <= 0;

  let showDateDisabledMessage = false;

  if (
    maxScheduledDays > 0 &&
    moment(maxDueDate).isBefore(moment().startOf("day").toDate())
  ) {
    showDateDisabledMessage = true;
  }

  const updatePaymentAllowedState = (currentFinalAmountDue: number) => {
    if (payableType.allowPartialPayment) {
      //overpayment allowed
      if (payableType.allowOverPayment) {
        // any amount can be paid
        if (currentFinalAmountDue > 0) {
          setPaymentAllowedState(PaymentAllowed.AnyAmount);
          return;
        } else {
          // any amount can be paid, also show banner on zero balance or credit
          setPaymentAllowedState(PaymentAllowed.AnyAmount);
          setBannerState({
            active: true,
            variant: "info",
            messageLabel: t("no-payment-due-overpayment-allowed"),
            withClose: true,
            withHyperlink: false,
            withIcon: true,
          });
        }
      }
      // overpayment not allowed
      else {
        if (currentFinalAmountDue > 0) {
          // Billed amount or less can be paid
          setPaymentAllowedState(PaymentAllowed.BilledAmountOrLess);
        } else {
          // No payment allowed
          // hide the payment details form
          setPaymentAllowedState(PaymentAllowed.NoPaymentAllowed);
          setBannerState({
            active: true,
            variant: "info",
            messageLabel: t("no-payment-due-overpayment-not-allowed"),
            withClose: true,
            withHyperlink: false,
            withIcon: true,
          });
        }
      }
    }
    // partial payment not allowed
    // no need to check overpayment value
    else {
      if (currentFinalAmountDue > 0) {
        // Billed amount only can be paid
        setPaymentAllowedState(PaymentAllowed.BilledAmountOnly);
      } else {
        // No payment allowed
        // hide the payment details form
        setPaymentAllowedState(PaymentAllowed.NoPaymentAllowed);
        setBannerState({
          active: true,
          variant: "info",
          messageLabel: t("no-payment-due-overpayment-not-allowed"),
          withClose: true,
          withHyperlink: false,
          withIcon: true,
        });
      }
    }
  };

  const setCashView = (method: string) => {
    if (method === "CashView") {
      setEmailBannerState(false);
      setSmsBannerState(false);
    } else if (method === "EmailModalSuccessClose") {
      setEmailBannerState(true);
      setSmsBannerState(false);
    } else if (method === "SmsModalSuccessClose") {
      setEmailBannerState(false);
      setSmsBannerState(true);
    } else {
      setEmailBannerState(false);
      setSmsBannerState(false);
    }
  };

  const handleEmailInputChange = (value: string) => {
    setEmailAddress(value);
  };

  const handleSmsInputChange = (value: string) => {
    setPhoneNumber(value);
  };

  // Account Messages

  const defaultBannerProps: Omit<BannerProps, "messageLabel"> = {
    active: true,
    variant: "info",
    withClose: true,
    withHyperlink: false,
    withIcon: true,
  };

  function prepareAccountMessages(messages: string[]): BannerProps[] {
    return messages.map((message) => ({
      ...defaultBannerProps,
      messageLabel: message,
    }));
  }

  // Method to get messages by keys
  const getMessagesByKeys = (keys: string[]): string[] => {
    return keys.reduce((messages: string[], key: string) => {
      if (labelSource.labels[key]) {
        messages.push(labelSource.labels[key]);
      }
      return messages;
    }, []);
  };

  function getAccountMessages(): string[] {
    if (
      //!isDataLoading &&
      payableSession?.payable &&
      payableSession?.payable.messageCodes.length > 0 &&
      labelSource.labels &&
      Object.keys(labelSource.labels).length > 0
    ) {
      const messages = getMessagesByKeys(
        payableSession?.payable?.messageCodes ?? []
      );
      return messages;
    }
    return [];
  }

  const accountMessages = prepareAccountMessages(getAccountMessages());

  // End Account Messages

  // if (isDataLoading) {
  //   return <DashboardSkeleton visible={true}></DashboardSkeleton>;
  // } else if (isErrorFetchingBalance) {
  //   return (
  //     <ErrorPayableDashboard
  //       visible={true}
  //       biller={finalBiller}
  //       payableKrn={payableKrn}
  //     ></ErrorPayableDashboard>
  //   );
  // }

  return (
    <div>
      {/* banner for generic info and error */}
      <div className="ez-pay-payable-banner">
        <Banner
          {...bannerState}
          onClose={() => setBannerState((prev) => ({ ...prev, active: false }))}
        ></Banner>

        {/* banner for account/restriction messages */}
        {accountMessages.map(
          (banner) =>
            banner.active && (
              <Banner
                key={banner.messageLabel}
                variant={banner.variant}
                messageLabel={banner.messageLabel}
                withClose={banner.withClose}
                withIcon={banner.withIcon}
              />
            )
        )}
        {emailBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={`${t("Cash barcode sent to " + emailAddress)}`}
            variant="success"
            withIcon
            withClose
          />
        )}
        {smsBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={`${t("Cash barcode sent to " + phoneNumber)}`}
            variant="success"
            withIcon
            withClose
          />
        )}
      </div>
      <div className="ez-pay-balance-frame">
        <PageLoader />
        <div className="ez-pay-balance-top">
          <PayableCardHeader />

          <div className="ez-pay-balance-row">
            <div className="ez-pay-balance-content-center-amount-due-row">
              {t("amount-due")}
              <Tooltip
                direction={isMobile ? TooltipEnums.TopLeft : TooltipEnums.Right}
                content={t("amount-due-tooltip")}
                classNames="amount-due-row-tooltip"
                data-cy="amount-due-tooltip"
              />
            </div>
            <div className="ez-pay-balance-content-center">
              <div
                className="ez-pay-balance-label-large"
                data-cy="final-amount-due"
              >
                {currencyFormatter.format(payableSession.payable.payableAmount)}
              </div>
            </div>

            {payableSession?.payable?.category === "account" ||
            payableSession?.payable?.category === "invoice" ? (
              <div className="ez-pay-balance-content-center-amount-due-row">
                {/* "{{payableName}} number" i.e. "Utility account number" or "Parking ticket number" */}
                {payableSession?.payable?.category === "account"
                  ? getLabel(labelSource.labels, "accountNumber")
                  : getLabel(labelSource.labels, "invoiceNumber")}
                :
                <span
                  className="ez-pay-balance-cell-value"
                  data-cy="final-due-date"
                >
                  {/* TODO: review this, there must be a better way to show the invoice/account number instead of having to cast the payable to the correct type. */}
                  {payableSession?.payable?.category === "account"
                    ? (payableSession?.payable as AccountPayable).accountNumber
                    : (payableSession?.payable as InvoicePayable).invoiceNumber}
                </span>
              </div>
            ) : null}

            <div className="ez-pay-balance-content-center-small">
              {payableSession?.payable?.category === "invoice" && (
                <div className="ez-pay-balance-content-cell">
                  {t("invoiced-amount")}:
                  <span
                    className="ez-pay-balance-cell-value"
                    data-cy="account-number"
                  >
                    {currencyFormatter.format(
                      (payableSession?.payable as InvoicePayable).billedAmount
                    )}
                  </span>
                </div>
              )}
              <div className="ez-pay-balance-content-cell">
                {t("due-date")}:
                <span
                  className="ez-pay-balance-cell-value"
                  data-cy="final-due-date"
                >
                  {payableSession?.payable?.dueDate
                    ? dateFormatter.shortIsoFormat(
                        payableSession?.payable?.dueDate
                      )
                    : ""}
                </span>
              </div>
            </div>

            <div>
              {!_.isEmpty(upcomingPayment) && (
                <>
                  <div className="ez-pay-balance-content-center-small">
                    <div className="ez-pay-scheduled-payment-content-cell">
                      {t("scheduled-payment")}{" "}
                      {dateFormatter.shortFormat(
                        new Date(upcomingPayment.paymentDate)
                      )}{" "}
                      <span className="ez-pay-schedule-payment-amount-cell-value">
                        {currencyFormatter.format(upcomingPayment.totalAmount)}
                      </span>
                    </div>
                  </div>
                  <br />
                </>
              )}
              {(!_.isEmpty(upcomingPayment) || recentPayments.length > 0) && (
                <div className="ez-pay-balance-content-center-small">
                  <button
                    className="ez-pay-button-link"
                    onClick={() =>
                      navigate(routing.payableRecentPayments.index())
                    }
                    data-cy="recent-payments"
                  >
                    {t("view-recent-payment")}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="ez-pay-balance-divider"></div>

          {checkIsCashOnly() && (
            <div>
              <CashView
                biller={finalBiller}
                // TODO: fix this, passing the KRN for now just so that there are no errors.
                // But we need to be passing the account number for account payables and may be
                // invoice number for the invoice payables OR may be it's okay to pass the payable KRN.
                // All that will be determined once we finalize how cash/RCP payments are supposed to work
                // with generic payables.
                accountNumber={payableSession?.payable?.krn}
                billerLogo={imageResources?.properties?.logoURL ?? ""}
                flowCallback={setCashView}
                realm={finalBiller.realm}
                emailInputChange={handleEmailInputChange}
                smsInputChange={handleSmsInputChange}
              ></CashView>
            </div>
          )}

          {(paymentAllowedState === PaymentAllowed.AnyAmount ||
            paymentAllowedState === PaymentAllowed.BilledAmountOnly ||
            paymentAllowedState === PaymentAllowed.BilledAmountOrLess) &&
            !checkIsCashOnly() && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="ez-pay-balance-no-frame">
                  {t("payment-details")}
                </div>
                {!payableType.allowPartialPayment ? null : isCashView ? null : (
                  <div className="ez-pay-balance-button-container">
                    <ButtonRow
                      buttonLabel={
                        currentPaymentAmount !==
                          payableSession?.payable?.payableAmount ||
                        payableSession?.payable?.payableAmount === 0
                          ? currencyFormatter.format(currentPaymentAmount)
                          : t("full-balance")
                      }
                      onClick={setAmountModalIsOpenToTrue}
                      staticLabel={`${t("amount")}`}
                      /* @ts-ignore */
                      dataCy="button-row-payment-amount"
                      data-testid="payment-amount-button-row"
                      errorText={
                        paymentErrorMessage && paymentErrorMessage.length > 0
                          ? paymentErrorMessage
                          : ""
                      }
                    />
                  </div>
                )}

                <div className="ez-pay-balance-button-container">
                  <ButtonRow
                    buttonLabel={paymentMethodLabel}
                    onClick={setMethodModalIsOpenToTrue}
                    staticLabel={`${t("method")}`}
                    {...(!isCashView && {
                      creditCardLogoVariant:
                        creditDebitCard !== null
                          ? castToCardType(
                              creditDebitCard?.cardType.toLowerCase()
                            )
                          : wallet &&
                            wallet.fundingOption === FundingOption.Card
                          ? castToCardType(
                              getCardNetworkByShortCode(
                                wallet.presentableMOPCode
                              ).toLocaleLowerCase()
                            )
                          : undefined,
                    })}
                    /* @ts-ignore */
                    dataCy="buttonw-row-payment-method"
                    data-testid="method-button-row"
                    errorText={
                      bankAccount == null &&
                      creditDebitCard == null &&
                      wallet == null
                        ? paymentMethodError
                        : ""
                    }
                  />
                </div>
                {maxScheduledDays > 0 && !isCashView ? (
                  <div className="ez-pay-balance-button-container">
                    <ButtonRow
                      disabled={futureDateDisabled}
                      // TODO:
                      buttonLabel={
                        dateFormatter.shortIsoFormat(
                          paymentIntentState.paymentDate,
                          "en-US"
                        ) === todayDateString
                          ? `${t("today", { ns: COMMON_NAMESPACE })}`
                          : dateFormatter.shortIsoFormat(
                              paymentIntentState.paymentDate,
                              "en-US"
                            )
                      }
                      onClick={paymentDateClicked}
                      staticLabel={`${t("date", { ns: COMMON_NAMESPACE })}`}
                      /* @ts-ignore */
                      dataCy="button-row-payment-date"
                      data-testid="payment-date-button"
                    />
                  </div>
                ) : null}

                {showDateDisabledMessage && (
                  <div className="ez-pay-balance-container-scheduled">
                    <div>
                      <img
                        src={CircleInfo}
                        alt="info icon"
                        width={16}
                        height={16}
                      />
                    </div>
                    <div>{t("scheduled-payment-disabled")}</div>
                  </div>
                )}
                {!_.isEmpty(upcomingPayment) && (
                  <div className="ez-pay-balance-button-container">
                    <Tooltip
                      direction={TooltipEnums.TopRight}
                      content={`${t("only-one-scheduled")}`}
                      data-cy="schedule-payment-tooltip"
                    />{" "}
                    {t("upcoming-payment-reminder", {
                      date: dateFormatter.shortFormat(
                        new Date(upcomingPayment.paymentDate)
                      ),
                      interpolation: { escapeValue: false },
                    })}
                    <button
                      onClick={() =>
                        navigate(routing.payableRecentPayments.index())
                      }
                      className="view-details"
                      data-cy="view-details"
                    >
                      {t("view-details")}
                    </button>
                  </div>
                )}

                {charities && charities.length > 0 && (
                  <div className="ez-pay-charity-container">
                    {charities.map((charity) => (
                      <Donation
                        key={charity.code}
                        charity={charity}
                        onCharityAmountSelected={(amount: number) =>
                          handleCharityAmountChange(charity.code, amount)
                        }
                        onCharitySelected={(isSelected: boolean) =>
                          handleCharitySelection(charity.code, isSelected)
                        }
                      />
                    ))}
                  </div>
                )}

                {charities && charities.length > 0 && charitiesState.map(
                  (charityState) =>
                    charityState.selected &&
                    charityState.amount > 0 && (
                      <div
                        key={charityState.charity.code}
                        className="ez-pay-donation-item-parent-container"
                      >
                        <div
                          className="ez-pay-donation-item-container"
                          data-testid="charities-container"
                        >
                          <div className="ez-pay-donation-item-row">
                            <div className="ez-pay-donation-item-label">
                              {t("donation-to", {
                                name: charityState.charity.name,
                              })}
                            </div>
                            <div className="ez-pay-donation-item-amount">
                              {currencyFormatter.format(charityState.amount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                )}

                <div className="ez-pay-payment-total-parent-container">
                  <div className="ez-pay-payment-total-container">
                    {/* TODO: review with design team if we need to show the amount due here again.
                    There's already a question asked about this in Figma but no decision on it yet. */}
                    <div className="ez-pay-payment-total-row">
                      <div className="ez-pay-payment-small-label">
                        {t("invoice-amount-due")}
                      </div>
                      <div
                        className={
                          paymentErrorMessage
                            ? "ez-pay-payment-total-amount-error"
                            : "ez-pay-payment-small-label"
                        }
                        data-cy="payment-due"
                      >
                        {currencyFormatter.format(currentPaymentAmount)}
                      </div>
                    </div>

                    <div className="ez-pay-payment-total-row">
                      <div className="ez-pay-payment-total-label">
                        {t("payment-total")}
                      </div>
                      <div
                        className={
                          paymentErrorMessage
                            ? "ez-pay-payment-total-amount-error"
                            : "ez-pay-payment-total-amount"
                        }
                        data-cy="payment-total"
                      >
                        {currencyFormatter.format(
                          currentPaymentAmount + charitiesTotal
                        )}
                      </div>
                    </div>

                    {totalErrorMessage && totalErrorMessage.length > 0 && (
                      <div className="ez-pay-balance-button-container">
                        <img
                          src={triangleWarning}
                          alt={totalErrorMessage}
                          className="floatLeft"
                        />
                        <div className="balance-amount-text-error">
                          {totalErrorMessage}
                        </div>
                      </div>
                    )}
                    <div
                      className="ez-pay-payment-total-label-info"
                      data-testid="process-payment-info"
                    >
                      {t("process-payment-info")}
                    </div>
                  </div>
                  {isCashView ? (
                    <div className="ez-pay-balance-row-noframe">
                      {/* @ts-ignore */}
                      <Button
                        className="validate-button"
                        onClick={() => [
                          console.log("generate barcode button clicked"),
                          navigate(routing.rcpBarcodeView.index()),
                        ]}
                        dataCy="button-generate-barcode"
                        data-testid="generate-barcode-button"
                      >
                        {t("generate-barcode")}
                      </Button>
                    </div>
                  ) : (
                    <div className="ez-pay-balance-row-noframe">
                      {paymentIntentState.paymentMethod == GooglePay &&
                        payableSession.googlePayConfiguration && (
                          <PayableGooglePayButtonWrapper
                            country="US"
                            currencyCode="USD"
                            clientId={finalBiller.clientId}
                            paymentIntentId={paymentIntentState.intentId}
                            amount={currentPaymentAmount + charitiesTotal}
                            reviewDigitalPayment={reviewDigitalPayment}
                            verifyPaymentAmount={
                              verifyPaymentAmountForDigitalWallet
                            }
                            onPrepareIntentError={
                              handlePrepareIntentErrorForDigitalWallet
                            }
                            config={payableSession.googlePayConfiguration}
                            realm={payableSession.instanceInfo.realm}
                            methodOfPayments={payableSession.methodOfPayments}
                          ></PayableGooglePayButtonWrapper>
                        )}
                      {paymentIntentState.paymentMethod == ApplePay && (
                        <PayableApplePayButtonWrapper
                          country="US"
                          currencyCode="USD"
                          clientId={finalBiller.clientId}
                          paymentIntentId={paymentIntentState.intentId}
                          amount={currentPaymentAmount + charitiesTotal}
                          reviewDigitalPayment={reviewDigitalPayment}
                          verifyPaymentAmount={
                            verifyPaymentAmountForDigitalWallet
                          }
                          onPrepareIntentError={
                            handlePrepareIntentErrorForDigitalWallet
                          }
                          methodOfPayments={payableSession.methodOfPayments}
                        ></PayableApplePayButtonWrapper>
                        // <ApplePayButtonWrapper
                        //   country="US"
                        //   currencyCode="USD"
                        //   clientId={finalBiller.clientId}
                        //   paymentIntentId={paymentIntentState.intentId}
                        //   amount={currentPaymentAmount}
                        //   reviewDigitalPayment={reviewDigitalPayment}
                        //   verifyPaymentAmount={
                        //     verifyPaymentAmountForDigitalWallet
                        //   }
                        //   onPrepareIntentError={
                        //     handlePrepareIntentErrorForDigitalWallet
                        //   }
                        //   methodOfPayments={payableSession.methodOfPayments}
                        // ></ApplePayButtonWrapper>
                      )}
                      {(paymentIntentState.paymentMethod == PayPal || paymentIntentState.paymentMethod == Venmo) &&
                        payableSession.paypalConfiguration &&
                        !methodModalIsOpen && payPalWrapper
                          // <PayablePayPalButtonWrapper
                          //   currency="USD"
                          //   amount={currentPaymentAmount + charitiesTotal}
                          //   paymentIntentId={payableSession.paymentIntentId}
                          //   clientId={finalBiller.clientId}
                          //   reviewDigitalPayment={reviewDigitalPayment}
                          //   config={payableSession.paypalConfiguration}
                          //   verifyPaymentAmount={
                          //     verifyPaymentAmountForDigitalWallet
                          //   }
                          //   onPrepareIntentError={
                          //     handlePrepareIntentErrorForDigitalWallet
                          //   }
                          // ></PayablePayPalButtonWrapper>
                        }
                      {paymentIntentState.paymentMethod != GooglePay &&
                        paymentIntentState.paymentMethod != PayPal &&
                        paymentIntentState.paymentMethod != ApplePay &&
                        paymentIntentState.paymentMethod != Venmo && (
                          <Button
                            className="validate-button"
                            onClick={handleContinueClick}
                            dataCy="button-continue"
                            data-testid="continue-button"
                          >
                            {t("continue", { ns: COMMON_NAMESPACE })}
                          </Button>
                        )}
                    </div>
                  )}
                </div>
                {isMobile && (
                  <Sheet
                    isOpen={amountModalIsOpen}
                    onClose={() => setAmountModalIsOpenToFalse}
                    detent="content-height"
                    disableDrag={true}
                  >
                    <Sheet.Container>
                      <Sheet.Header>
                        <div className="react-modal-sheet-header">
                          <div
                            className="help-modal-header-text"
                            data-testid="select-amount-modal-header"
                          >
                            {t("select-amount")}
                          </div>
                          <button
                            className="modal-close"
                            onClick={setAmountModalIsOpenToFalse}
                            aria-label={`${t("close-modal", {
                              ns: COMMON_NAMESPACE,
                            })}`}
                            data-cy="modal-close-x-payment-amount"
                          >
                            <img
                              src={XButton}
                              alt={`${t("close-modal", {
                                ns: COMMON_NAMESPACE,
                              })}`}
                              className="modal-close-x"
                            />
                          </button>
                        </div>
                      </Sheet.Header>
                      <Sheet.Content>
                        <PaymentAmountModal
                          allowPartialPayment={payableType.allowPartialPayment}
                          allowOverPayment={payableType.allowOverPayment}
                          fullBalanceAmount={
                            payableSession?.payable?.payableAmount
                          }
                          updateCallBack={updateCurrentPaymentAmount}
                        />
                      </Sheet.Content>
                    </Sheet.Container>
                    <Sheet.Backdrop />
                  </Sheet>
                )}
                {!isMobile && (
                  // @ts-ignore
                  <Modal
                    isOpen={amountModalIsOpen}
                    ariaHideApp={false}
                    className="modal-frame-help"
                    shouldCloseOnOverlayClick={false}
                    onRequestClose={setAmountModalIsOpenToFalse}
                    style={customStyles}
                  >
                    <div className="modal-header">
                      <div
                        className="help-modal-header-text"
                        data-testid="select-amount-modal-header"
                      >
                        {t("select-amount")}
                      </div>
                      <button
                        className="modal-close"
                        onClick={setAmountModalIsOpenToFalse}
                        aria-label={`${t("close-modal", {
                          ns: COMMON_NAMESPACE,
                        })}`}
                        data-cy="modal-close-x-payment-amount"
                      >
                        <img
                          src={XButton}
                          alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                          className="modal-close-x"
                        />
                      </button>
                    </div>
                    <PaymentAmountModal
                      allowPartialPayment={payableType?.allowPartialPayment}
                      allowOverPayment={payableType?.allowOverPayment}
                      fullBalanceAmount={payableSession?.payable?.payableAmount}
                      updateCallBack={updateCurrentPaymentAmount}
                    />
                  </Modal>
                )}
                {isMobile && (
                  <Sheet
                    isOpen={methodModalIsOpen}
                    onClose={() => setMethodModalIsOpenToFalse}
                    detent="content-height"
                    disableDrag={true}
                  >
                    <Sheet.Container>
                      <Sheet.Header>
                        <div
                          className="react-modal-sheet-header"
                          data-testid="select-method-modal"
                        >
                          {displayMethodBack && (
                            <button
                              onClick={() => {
                                handleMethodChange("SelectPaymentMethodModal");
                              }}
                              className="modal-close-back"
                            >
                              <FontAwesomeIcon icon={faArrowLeft} size="xl" />
                            </button>
                          )}
                          <div className="help-modal-header-text">
                            {t("select-method")}
                          </div>
                          <button
                            className="modal-close"
                            onClick={setMethodModalIsOpenToFalse}
                            aria-label={`${t("close-modal", {
                              ns: COMMON_NAMESPACE,
                            })}`}
                            data-cy="modal-close-x-payment-method"
                          >
                            <img
                              src={XButton}
                              alt={`${t("close-modal", {
                                ns: COMMON_NAMESPACE,
                              })}`}
                              className="modal-close-x"
                            />
                          </button>
                        </div>
                      </Sheet.Header>
                      <Sheet.Content style={{ overflow: "auto" }}>
                        <CurrentMethodComponent
                          billerLogo={imageResources?.properties?.logoURL}
                          flowCallback={handleMethodChange}
                          // TODO: there doesn't seem to be any component in "methodComponents"
                          // that accepts a payable KRN, so safe to remove it from here.
                          // payableKrn={payableKrn}
                          realm={finalBiller.realm}
                          biller={finalBiller}
                          amount={currentPaymentAmount}
                          billerConfiguration={billerConfiguration}
                          termsUrl={
                            externalLinks?.properties?.termsAndConditions
                          }
                          // TODO: probably safe to remove this as none of the components
                          // in "methodComponents" have a userAccount prop.
                          // userAccount={mockAccount}
                          closeModalCallBack={setMethodModalIsOpenToFalse}
                          reviewDigitalPayment={reviewDigitalPayment}
                          verifyPaymentAmount={
                            verifyPaymentAmountForDigitalWallet
                          }
                          onPrepareIntentError={
                            handlePrepareIntentErrorForDigitalWallet
                          }
                          showManagePaymentMethodsModal={() =>
                            setManagePaymentMethodModalIsOpen(true)
                          }
                          isOpen={setRetailerModalStatus}
                          setIsCashView={setIsCashView}
                        />
                      </Sheet.Content>
                    </Sheet.Container>
                    <Sheet.Backdrop />
                  </Sheet>
                )}
                {!isMobile && (
                  // @ts-ignore
                  <Modal
                    isOpen={methodModalIsOpen}
                    ariaHideApp={false}
                    className="modal-frame-help"
                    shouldCloseOnOverlayClick={false}
                    onRequestClose={setMethodModalIsOpenToFalse}
                    style={customStyles}
                  >
                    <div
                      className="modal-header-with-back-button"
                      data-testid="select-method-modal"
                    >
                      {displayMethodBack && (
                        <button
                          onClick={() => {
                            handleMethodChange("SelectPaymentMethodModal");
                          }}
                          className="modal-close-back"
                        >
                          <FontAwesomeIcon icon={faArrowLeft} size="xl" />
                        </button>
                      )}
                      <div className="help-modal-header-text">
                        {isCashView ? t("cash-method") : t("select-method")}
                      </div>
                      <button
                        className="modal-close"
                        onClick={setMethodModalIsOpenToFalse}
                        aria-label={`${t("close-modal", {
                          ns: COMMON_NAMESPACE,
                        })}`}
                        data-cy="modal-close-x--payment-method"
                      >
                        <img
                          src={XButton}
                          alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                          className="modal-close-x"
                        />
                      </button>
                    </div>
                    <CurrentMethodComponent
                      billerLogo={imageResources?.properties?.logoURL}
                      flowCallback={handleMethodChange}
                      // TODO: there doesn't seem to be any component in "methodComponents"
                      // that accepts a payable KRN, so safe to remove it from here.
                      // payableKrn={payableKrn}
                      biller={finalBiller}
                      amount={currentPaymentAmount}
                      billerConfiguration={billerConfiguration}
                      termsUrl={externalLinks?.properties?.termsAndConditions}
                      // TODO: probably safe to remove this as none of the components
                      // in "methodComponents" have a userAccount prop.
                      // userAccount={mockAccount}
                      closeModalCallBack={setMethodModalIsOpenToFalse}
                      reviewDigitalPayment={reviewDigitalPayment}
                      verifyPaymentAmount={verifyPaymentAmountForDigitalWallet}
                      onPrepareIntentError={
                        handlePrepareIntentErrorForDigitalWallet
                      }
                      showManagePaymentMethodsModal={() =>
                        setManagePaymentMethodModalIsOpen(true)
                      }
                      isOpen={setRetailerModalStatus}
                      setIsCashView={setIsCashView}
                    />
                  </Modal>
                )}

                <ManagePaymentMethodsModal
                  isOpen={managePaymentMethodModalIsOpen}
                  onClose={() => setManagePaymentMethodModalIsOpen(false)}
                  isPayable
                />
                {/* TODO: review if the payableSession.payable.dueDate will have timezone information and how it will be displayed to the user. */}
                <PaymentDateModal
                  dueDate={
                    payableSession?.payable?.dueDate
                      ? new Date(payableSession?.payable?.dueDate)
                      : new Date()
                  }
                  daysAfter={maxScheduledDays < 0 ? 0 : maxScheduledDays}
                  isOpen={dateModalIsOpen}
                  onClose={() => setDateModalIsOpen(false)}
                  biller={finalBiller}
                ></PaymentDateModal>
              </form>
            )}
          {paymentAllowedState === PaymentAllowed.NoPaymentAllowed &&
            !isDataLoading && (
              <div className="ez-pay-balance-row-noframe">
                <Button
                  type="button"
                  className="validate-button"
                  onClick={() => {
                    IamApi.ezpay.logout();
                    dispatch({ type: USER_LOGOUT });
                    dispatch(setPayableSession(undefined));
                  }}
                  dataCy="button-validate"
                >
                  {t("change-payable", {
                    ns: PAYMENT_RECEIPT_NAMESPACE,
                    name: finalBiller?.billerName ?? "",
                  })}
                </Button>
              </div>
            )}
          <ContactAndTocFooter biller={finalBiller} />
        </div>
      </div>
    </div>
  );
};
export default PayableDashboard;
